import type { EditableImageListRecord, PendingImage } from '../../types/index.ts';
import { arrayMove } from '@dnd-kit/helpers';
import { noop } from 'es-toolkit';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as uuid from 'uuid';
import { createImageObjectUrl } from '@_/utils-frontend';

export interface UseEditableImageListOptions {
  initialItems?: EditableImageListRecord[];
  maxImages?: number;
  onUploadFile: (file: File) => Promise<string>;
}

export function useEditableImageList({
  initialItems,
  maxImages,
  onUploadFile
}: UseEditableImageListOptions) {
  const [currentItems, setCurrentItems] = useState<EditableImageListRecord[]>(() =>
    initialItems ? [...initialItems] : []
  );

  useEffect(() => {
    setCurrentItems(initialItems ? [...initialItems] : []);
  }, [initialItems]);

  const newItems = useMemo<PendingImage[]>(
    () => currentItems.filter(item => item.isPending) as PendingImage[],
    [currentItems]
  );

  const uploadingItems = useMemo<PendingImage[]>(
    () => newItems.filter(item => item.isPending && item.upload.inProgress),
    [newItems]
  );

  const onUploadFileRef = useRef(onUploadFile);
  onUploadFileRef.current = onUploadFile;

  const addItem = useCallback(async (file: File) => {
    const imageUrl = await createImageObjectUrl(file);

    const newItem: PendingImage = {
      id: uuid.v4(),
      isPending: true,
      imageUrl,
      upload: {
        error: null,
        fileName: null,
        inProgress: true
      }
    };

    setCurrentItems(currItems => [...currItems, newItem]);

    // Initiate upload request
    onUploadFileRef
      .current(file)
      .then(fileName => {
        // Update pending image with uploaded file name
        setCurrentItems(prevItems =>
          prevItems.map(prevItem => {
            if (prevItem.id !== newItem.id) {
              return prevItem;
            }

            return {
              ...prevItem,
              upload: {
                error: null,
                fileName,
                inProgress: false
              }
            } as PendingImage;
          })
        );
      })
      .catch(error => {
        // Record error if uploading of image fails
        setCurrentItems(prevItems =>
          prevItems.map(prevItem => {
            if (prevItem.id !== newItem.id) {
              return prevItem;
            }

            return {
              ...prevItem,
              upload: {
                error,
                fileName: null,
                inProgress: false
              }
            } as PendingImage;
          })
        );
      });
  }, []);

  const removeItem = useCallback((itemId: string) => {
    setCurrentItems(currItems => currItems.filter(item => item.id !== itemId));
  }, []);

  const moveItem = useCallback((initialIndex: number, index: number) => {
    setCurrentItems(currItems => arrayMove([...currItems], initialIndex, index));
  }, []);

  const reset = useCallback(() => {
    setCurrentItems(initialItems ? [...initialItems] : []);
  }, [initialItems]);

  const hasChanged =
    initialItems?.length !== currentItems.length ||
    (initialItems && !initialItems.every((item, index) => currentItems[index].id === item.id));

  return {
    currentItems,
    hasChanged,
    listeners: {
      onAddItem: typeof maxImages === 'number' && currentItems.length < maxImages ? addItem : noop,
      onRemoveItem: removeItem,
      onMoveItem: moveItem
    },
    newItems,
    reset,
    uploadingItems
  };
}
